<template>
  <div>
    <v-row v-if="isLocationOriginal" class="mb-4">
      <v-col cols="12" md="6">
        <gg-card-title class="pb-0">
          {{ title }}
        </gg-card-title>
      </v-col>
      <v-col cols="12" md="6" class="text-right mt-2">
        <div>
          <gg-btn class="mt-1 mr-1" right :to="{ name: 'support_ticketnew' }">
            Open new ticket
          </gg-btn>
        </div>
      </v-col>
    </v-row>
    <gg-loading :loading="!ticketsPopulated" type="table" class="dashboard-fill-height">
      <div>
        <gg-card height="100%">
          <gg-card-header>
            <template v-if="isLocationOriginal">
              {{ subtitle }}
              <gg-btn class="float-right" :disabled="loading" @click="getTicketsData">
                <v-icon>
                  mdi-refresh
                </v-icon>
              </gg-btn>
            </template>
            <v-row v-if="!isLocationOriginal">
              <v-col cols="6" md="4">
                {{ title }}
              </v-col>
              <v-col cols="6" md="8" class="text-right">
                <div class="hidden-xs-only">
                  <gg-btn secondary class="mx-1" :disabled="loading" right @click="getTicketsData">
                    <v-icon>
                      mdi-refresh
                    </v-icon>
                    <span class="visually-hidden">Refresh tickets</span>
                  </gg-btn>
                  <gg-btn class="mx-1" right :to="{ name: 'support_ticketnew' }">
                    Open New Ticket
                  </gg-btn>
                  <gg-btn secondary class="mx-1" right :to="{ name: 'support_ticketlist' }">
                    All tickets
                  </gg-btn>
                </div>
                <div class="hidden-sm-and-up py-0">
                  <v-menu bottom left offset-y>
                    <template v-slot:activator="{ on }">
                      <gg-btn v-on="on" class="ml-0" secondary>
                        Actions <v-icon right dark>mdi-menu-down</v-icon>
                      </gg-btn>
                    </template>
                    <v-list>
                      <v-list-item :to="{ name: 'support_ticketnew' }">
                        <v-list-item-title>Open new ticket</v-list-item-title>
                      </v-list-item>
                      <v-list-item :to="{ name: 'support_ticketlist' }">
                        <v-list-item-title>All tickets</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="getTicketsData(true)">
                        <v-list-item-title>Refresh tickets list</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </v-col>
            </v-row>
          </gg-card-header>
          <gg-card-text>
            <services-search v-if="isLocationOriginal" v-model="search" />
            <v-data-table
              :hide-default-footer="false"
              :headers="headers"
              :items="formatted_tickets"
              :loading="loading"
              :search="search"
              sort-by="last_activity"
              sort-desc
              :footer-props="{ itemsPerPageOptions: this.pagination }"
            >
              <template v-slot:item.department="{ item }">
                <div>
                  <v-icon>mdi-folder</v-icon>
                  {{ item.department }}
                </div>
              </template>
              <template v-slot:item.title="{ item }">
                <router-link :to="`/support/ticket/${item.ticket_id}`">
                  <span class="d-none d-sm-flex">{{ item.title }}</span>
                  <span class="d-flex d-sm-none">{{ textTruncate(item.title_mobile, 20) }}</span>
                </router-link> <br>
                <span v-show="compact">
                <v-icon>mdi-folder</v-icon>
                {{ item.department }}
              </span>
              </template>
              <template v-slot:item.related_service_text="{ item }">
                <a href="#" @click.prevent="navigateTo(item.related_service)">
                  {{ item.related_service_text }}
                </a>
                <router-link v-if="item.sharedaccount || item.sharedaccess" :to="{ name: 'profile_access' }" class="pl-1">
                  <v-icon small>mdi-account-multiple</v-icon>
                </router-link>
              </template>
              <template v-slot:item.status="{ item }">
                <div>
                  <v-chip v-if="item.status == 'In Progress'" class="ma-2" label color="green" text-color="white">
                    {{ item.status }}
                  </v-chip>
                  <v-chip v-else-if="item.status == 'Closed'" class="ma-2" label color="red" text-color="white">
                    {{ item.status }}
                  </v-chip>
                  <v-chip v-else class="ma-2" label>
                    {{ item.status }}
                  </v-chip>
                </div>
              </template>
            </v-data-table>
          </gg-card-text>
        </gg-card>
      </div>
    </gg-loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { textTruncate } from 'Helpers/helpers'
import ServicesSearch from 'Components/Services/Hosting/ServicesPage/ServicesSearch'

export default {
  name: 'TicketsList',
  components: {
    ServicesSearch
  },
  props: {
    title: {
      type: String,
      default: 'Support'
    },
    subtitle: {
      type: String,
      default: 'My Support Tickets'
    },
    location: {
      type: String,
      default: 'original' // or dashboard
    },
    pagination: {
      type: Array,
      default () {
        return []
      }
    },
    excludeStatuses: {
      type: Array,
      default () {
        return []
      }
    },
    compact: {
      type: Boolean,
      default: false
    },
  },
  data: () => ({
    loading_initial: false,
    loading: false,
    search: ''
  }),
  computed: {
    ...mapGetters(['ticketsPopulated', 'tickets']),
    headers () {
      if (this.compact) {
        return [
          { text: 'Ticket', align: 'left', value: 'title', class: 'table-heading-sm' },
          { text: 'Status', align: 'left', value: 'status' },
        ]
      }
      return [
        { text: 'Department', align: 'left', value: 'department' },
        { text: 'Ticket', align: 'left', value: 'title', class: 'table-heading-sm' },
        { text: 'Related Service', align: 'left', value: 'related_service_text', class: 'table-heading-sm' },
        { text: 'Status', align: 'left', value: 'status' },
        { text: 'Last Activity', align: 'left', value: 'last_activity' },
      ]
    },
    formatted_tickets () {
      let tickets

      tickets = this.tickets.map(item => {
        return {
          ticket_id: item.ticketmaskid,
          department: item.departmenttitle,
          title: `[# ${item.ticketmaskid}] ${item.subject}`,
          title_mobile: item.subject,
          status: item.ticketstatustitle,
          last_activity: item.lastactivity,
          sharedaccount: item.sharedaccount,
          sharedaccess: item.sharedaccess,
          related_service: item.related_service,
          related_service_type: item.related_service_type,
          related_service_text: item.related_service_text
        }
      }).filter(ticket => {
        return this.excludeStatuses.indexOf(ticket.status) === -1
      })

      return tickets
    },
    isLocationOriginal () {
      return this.location === 'original'
    }
  },
  methods: {
    ...mapActions(['getTickets', 'navigateTo', 'reportError']),
    textTruncate,
    async getTicketsData () {
      this.loading = true
      try {
        await this.getTickets()
        this.loading = false
      } catch (e) {
        this.reportError(e)
      }
    },
  }
}
</script>
